import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId, MarketId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { getMarketName, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { useMemo } from 'react';
import type { Warning } from '@/components/common/WarningIcon';
import type { UserBorrowPosition } from '@/hooks/positions/userBorrowPositionsQuery';
import { getMarketWithPoints } from '@/components/charts/helpers/getPoints';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getParsedValue } from '@/helpers/formatters';
import { getOraclePriceFeedDecimals } from '@/helpers/oracle';
import { getMarketRewards } from '@/helpers/rewards';
import { getMarketWarningMessage } from '@/helpers/warningMessages';
import { apiWarningLevelToEnum, isApiWarningLevel } from '@/helpers/warnings';
import { toBorrowPositionInputToken } from '@/hooks/positions/userBorrowPositionsQuery';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getBorrowPositionLiquidationPrice } from '../helpers/liquidationPrice';
export interface BorrowPositionsTableRowsProps {
  borrowPositions: UserBorrowPosition[];
  userAddress?: Address;
}
export const useBorrowPositionsTableRows = t0 => {
  const $ = _c(11);
  const {
    borrowPositions,
    userAddress
  } = t0;
  const {
    positionHistory: isPositionHistoryEnabled,
    showPoints
  } = useFeatureFlags();
  const {
    targetChainId
  } = useWeb3Context();
  let t1;
  if ($[0] !== borrowPositions || $[1] !== isPositionHistoryEnabled || $[2] !== showPoints || $[3] !== targetChainId || $[4] !== userAddress) {
    let t2;
    if ($[6] !== isPositionHistoryEnabled || $[7] !== showPoints || $[8] !== targetChainId || $[9] !== userAddress) {
      t2 = borrowPosition => {
        const {
          market
        } = borrowPosition;
        const marketId = market.marketId as MarketId;
        const loanAsset = parseApiAsset(toBorrowPositionInputToken(market.loanAsset));
        const collateralAsset = parseApiAsset(market.collateralAsset ? {
          ...toBorrowPositionInputToken(market.collateralAsset),
          yield: market.collateralAsset.yieldApy
        } : null);
        const collateralAssetYieldApy = safeParseNumber(market.collateralAsset?.yieldApy ?? 0);
        const warnings = [];
        for (const {
          type,
          level,
          content,
          badDebtUsd,
          badDebtAssets
        } of market.warnings) {
          if (!isApiWarningLevel(level)) {
            continue;
          }
          const message = getMarketWarningMessage({
            type,
            level,
            metadata: {
              content,
              badDebtUsd,
              badDebtAssets
            }
          }, loanAsset);
          if (message) {
            warnings.push({
              level: apiWarningLevelToEnum(level),
              message,
              warningType: type
            });
          }
        }
        const rewards = getMarketRewards({
          state: {
            rewards: market.borrowRewards.map(_temp)
          },
          loanAsset: toBorrowPositionInputToken(market.loanAsset)
        }, "borrow");
        const oracleDecimals = getOraclePriceFeedDecimals({
          loanTokenDecimals: loanAsset.decimals,
          collateralTokenDecimals: collateralAsset?.decimals ?? 18
        });
        const avgBorrowApy = getParsedValue(market.apy.native, safeParseNumber);
        const avgNetBorrowApy = getParsedValue(market.apy.net, safeParseNumber);
        const points = showPoints ? getMarketWithPoints(targetChainId, marketId, collateralAsset?.address).filter(isDefined) : [];
        const ltv = getParsedValue(borrowPosition.ltv, safeParseNumber);
        const lltv = BigInt(market.lltv);
        const collateral = toBigInt(borrowPosition.collateral);
        const borrowAssets = toBigInt(borrowPosition.borrowAssets);
        const liquidationPrice = getBorrowPositionLiquidationPrice({
          collateral,
          borrowAssets,
          lltv
        });
        const borrowPnl = toBigInt(borrowPosition.borrowPnl);
        const accruedInterestAsset = borrowPnl != null ? borrowPnl < ZERO ? -borrowPnl : borrowPnl : null;
        const accruedInterestUsd = getParsedValue(borrowPosition.borrowPnlUsd != null ? Math.abs(borrowPosition.borrowPnlUsd) : null, safeParseNumber);
        return {
          id: `${market.chainId}-${marketId}`,
          isNew: false,
          market: {
            ...market,
            marketId,
            loanAsset: {
              ...market.loanAsset,
              symbol: market.loanAsset.symbol ?? UNKNOWN_SYMBOL
            },
            collateralAsset: market.collateralAsset && {
              ...market.collateralAsset,
              symbol: market.collateralAsset.symbol ?? UNKNOWN_SYMBOL
            }
          },
          collateral: {
            amount: collateral,
            value: borrowPosition.collateralUsd,
            token: collateralAsset,
            netApy: collateralAssetYieldApy,
            warnings
          },
          borrow: {
            chainId: market.chainId as ChainId,
            amount: borrowAssets,
            value: borrowPosition.borrowAssetsUsd,
            token: loanAsset,
            userAddress,
            isPositionHistoryEnabled,
            accruedInterest: {
              assetValue: accruedInterestAsset,
              usdValue: accruedInterestUsd
            }
          },
          borrowShares: toBigInt(borrowPosition.borrowShares),
          netApy: {
            avgNetBorrowApy,
            avgBorrowApy,
            rewards,
            points
          },
          health: {
            healthFactor: ltv === null ? null : borrowPosition.healthFactor,
            ltv,
            lltv,
            marketSymbol: getMarketName({
              loanAsset,
              collateralAsset,
              maxLength: 15
            }),
            decimals: oracleDecimals,
            liquidationPrice
          }
        };
      };
      $[6] = isPositionHistoryEnabled;
      $[7] = showPoints;
      $[8] = targetChainId;
      $[9] = userAddress;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = borrowPositions.map(t2).filter(isDefined);
    $[0] = borrowPositions;
    $[1] = isPositionHistoryEnabled;
    $[2] = showPoints;
    $[3] = targetChainId;
    $[4] = userAddress;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
function _temp(t0) {
  const {
    asset,
    borrowApr
  } = t0;
  return {
    borrowApr,
    asset: toBorrowPositionInputToken(asset)
  };
}